import React, { useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import ModalForm from 'core/elements/modal/ModalForm'
import { generateApiErrorPayload } from 'api-client/helpers'
import { MessageTypes } from 'core/components/notifications/model'
import { useToast } from 'core/providers/ToastProvider'
import { unmountISOAction } from './actions'
import { dispatchVmIsoOperationCompleted, VmIsoOperationType } from './isoMountEvents'
import { getExtraDriveVolumesInfo } from './helpers'
import AsyncDropdown from 'core/elements/dropdown/AsyncDropdown'
import { getVolumeNames } from './isoOperations'

interface Props {
  rows: any[]
  onClose: (success?: boolean) => void
}

export default function UnmountISODialog({ rows: vms, onClose }: Props) {
  const vm = vms?.[0]
  const classes = useStyles()
  const showToast = useToast()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [selectedVolumeId, setSelectedVolumeId] = useState<string | null>(null)
  const [volumeNames, setVolumeNames] = useState<Record<string, string>>({})
  const [loadingVolumeNames, setLoadingVolumeNames] = useState(false)

  const { volumes: extraDriveVolumes } = getExtraDriveVolumesInfo(vm)

  useEffect(() => {
    const fetchVolumeNames = async () => {
      const ids = extraDriveVolumes.map((v) => v?.id).filter(Boolean)
      if (!ids.length) return
      setLoadingVolumeNames(true)
      try {
        const names = await getVolumeNames(ids)
        setVolumeNames(names)
      } finally {
        setLoadingVolumeNames(false)
      }
    }
    fetchVolumeNames()
  }, [extraDriveVolumes.map((v) => v?.id).join(',')])
  // Pre-select: if only one extra drive, auto-select it; otherwise wait for user
  const targetVolumeId =
    selectedVolumeId || (extraDriveVolumes.length === 1 ? extraDriveVolumes[0]?.id : null)

  const targetVolumeName =
    (targetVolumeId && volumeNames[targetVolumeId]) ||
    extraDriveVolumes.find((v) => v?.id === targetVolumeId)?.name ||
    null

  const handleSubmit = useCallback(async () => {
    if (!targetVolumeId) {
      setError({ title: '', message: 'Please select an ISO volume to unmount' })
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const result = await unmountISOAction({ vmId: vm?.id, volumeId: targetVolumeId })
      dispatchVmIsoOperationCompleted({
        vmId: vm?.id,
        operation: VmIsoOperationType.Unmount,
        volumeId: targetVolumeId,
      })

      onClose(true)
    } catch (err) {
      const message =
        generateApiErrorPayload(err, '', 'Failed to unmount ISO')?.message ||
        'Failed to unmount ISO'
      setError({ title: 'Unmount ISO Failed', message })
    } finally {
      setSubmitting(false)
    }
  }, [onClose, showToast, vm, targetVolumeId])

  return (
    <ModalForm
      open
      panel="dialog"
      title="Unmount ISO"
      onSubmit={handleSubmit}
      onClose={onClose}
      submitting={submitting}
      error={error}
      submitTitle="Unmount ISO"
      maxWidth={600}
    >
      <div className={classes.body}>
        <Text variant="body2">
          Select the ISO drive to detach from <strong>{vm?.name || vm?.id}</strong>.
        </Text>
        <AsyncDropdown
          label="ISO Drive"
          items={extraDriveVolumes.map((v) => ({
            label: volumeNames[v?.id] || v?.name || v?.id,
            value: v?.id,
          }))}
          value={targetVolumeId}
          onChange={(value) => setSelectedVolumeId(value as string)}
          loading={loadingVolumeNames}
          compact={false}
        />
        {targetVolumeName && (
          <Text variant="body2">
            Selected: <strong>{targetVolumeName}</strong>
          </Text>
        )}
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  body: {
    display: 'grid',
    gap: theme.spacing(1.5),
  },
}))
